import React, { useEffect, useState } from "react";
import api from "../../utils/api";
import { useRestaurantConfig } from "../../utils/restaurantConfig";
import { getCurrencySymbol } from "../../helper/formatCurrency";
import { REPAIR_BLOCKED_STATUSES } from "./utils/constants";

const OrderPopup = ({
  popupOrder,
  rows,
  driverList,
  driverId,
  driverName,
  driverMobile,
  useNewDriver,
  isFinalStagePopup,
  reasons,
  selectedRejectReason,
  isRejecting,
  setPopupOrder,
  setDriverId,
  setDriverName,
  setDriverMobile,
  setUseNewDriver,
  setIsRejecting,
  setSelectedRejectReason,
  updatePopupOrderStatus,
  updatePopupOrderStatusReject,
  getNextStatuses,
  toast,
  setSelectedOrder,
}) => {
  const [orderCount, setOrderCount] = useState(1);
  const [isLoadingCount, setIsLoadingCount] = useState(false);
  const { locale, gstFraction, taxLabel } = useRestaurantConfig();
  const currencySymbol = getCurrencySymbol(locale);
  // Only allow repair before delivery lifecycle completes




  useEffect(() => {
    const fetchOrderCount = async () => {
      if (!popupOrder?._id) return;

      // Guest user → always 1
      if (!popupOrder.userId) {
        setOrderCount(1);
        return;
      }

      try {
        setIsLoadingCount(true);
        const res = await api.get(`/orders/count-by-user/${popupOrder._id}`);
        setOrderCount(res.data?.count || 1);
      } catch (err) {
        console.error("Failed to fetch order count", err);
        setOrderCount(1);
      } finally {
        setIsLoadingCount(false);
      }
    };

    fetchOrderCount();
  }, [popupOrder]);

  // Reset rejecting state when order changes
  useEffect(() => {
    setIsRejecting(false);
    setSelectedRejectReason("");
  }, [popupOrder, setIsRejecting, setSelectedRejectReason]);
  if (!popupOrder) return null;
  // ===== Display Control Flags =====
  const showCustomerDetails = true; // now always visible


  const products = rows || [];

  const gstRate = popupOrder?.taxrate ?? gstFraction ?? 0.05;
  // Check if products were modified
  const isEditing =
    rows?.length !== popupOrder?.products?.length ||
    rows?.some((r, i) => {
      const original = popupOrder?.products?.[i];
      return (
        !original ||
        r.quantity !== original.quantity ||
        r.price !== original.price ||
        r.offerPrice !== original.offerPrice
      );
    });

  // Always calculate from rows
  const calculatedSubTotal = rows.reduce(
    (sum, p) =>
      sum +
      Number(p.offerPrice || p.price || 0) *
      Number(p.quantity || 0),
    0
  );

  const calculatedPackaging = rows.reduce(
    (sum, p) =>
      sum +
      Number(p.packageCharge || 0) *
      Number(p.quantity || 0),
    0
  );

  const calculatedGST = rows.reduce((sum, p) => {
    const finalPrice = Number(p.offerPrice || p.price || 0);
    const qty = Number(p.quantity || 0);

    // If explicitly false → skip tax
    if (p.isTaxable === false) return sum;

    return sum + finalPrice * qty * gstRate;
  }, 0);
  const subTotal = isEditing
    ? calculatedSubTotal
    : popupOrder?.subTotal ?? calculatedSubTotal;

  const totalPackageCharge = isEditing
    ? calculatedPackaging
    : popupOrder?.packagingTotal ?? calculatedPackaging;

  const gstAmount = isEditing
    ? calculatedGST
    : popupOrder?.taxAmount ?? calculatedGST;

  const totalWithGST = subTotal + gstAmount + totalPackageCharge;
  return (
    <div className="order-popup-overlay">
      <div className="order-popup-box">
        {/* Header */}
        <div className="popup-header">
          <h3>Manage Order: {popupOrder.orderId}</h3>
          <button className="close-btn" onClick={() => setPopupOrder(null)}>
            ✖
          </button>
        </div>

        <div className="popup-scroll-body">
        {showCustomerDetails && (
          <div className="popup-section inline-display">
            <span className="popup-label">Customer Name:</span>
            <span className="popup-value">{popupOrder.name || "Guest"}</span>
          </div>
        )}

        {showCustomerDetails && (

          <div className="popup-section inline-display">
            <span className="popup-label">Delivery Address:</span>
            <span className="popup-value">
              {popupOrder.address
                ? `${popupOrder.address.replace(/,+\s*$/, "")}${popupOrder.pincode ? `, ${popupOrder.pincode}` : ""
                }`
                : "No address provided"}
            </span>
          </div>
        )}
        {showCustomerDetails && (

          <div className="popup-section inline-display">
            <span className="popup-label">Total Times:</span>
            <span className="popup-value">
              {isLoadingCount ? "Loading..." : orderCount}
            </span>
          </div>
        )}

        {popupOrder.assignedDriver && (
          <div className="popup-section inline-display" style={{ marginTop: "8px" }}>
            <span className="popup-label">Driver Details:</span>
            <span className="popup-value">
              {popupOrder.assignedDriver?.name} - {popupOrder.assignedDriver?.mobile}
            </span>
          </div>
        )}


        {popupOrder.status === "reject" && popupOrder.rejectReason && (
          <div className="popup-section inline-display">
            <span className="popup-label">Reject Reason:</span>
            <span className="popup-value" style={{ color: "red", fontWeight: "600" }}>
              {popupOrder.rejectReason}
            </span>
          </div>
        )}


        {/* Ordered Products */}
        {rows.length > 0 && (
          <div className="popup-section">
            <label className="popup-label">Order Details:</label>

            <div className="popup-product-table-wrapper">
              <table className="popup-product-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Qty</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((product, index) => {
                    const finalPrice = product.offerPrice || product.price || 0;
                    const total = Number(product.quantity) * Number(finalPrice);

                    return (
                      <tr key={index}>
                        <td>
                          {product.productTitle?.split(",")[0] ||
                            "Unnamed Product"}
                          {product.addOns?.length > 0 && (
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#4f46e5",
                                marginTop: "2px",
                              }}
                            >
                              +{" "}
                              {product.addOns
                                .map((a) => (a.quantity > 1 ? `${a.name} x${a.quantity}` : a.name))
                                .join(", ")}
                            </div>
                          )}
                          {product.note && (
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#6c757d",
                                fontStyle: "italic",
                                marginTop: "2px",
                              }}
                            >
                              Note: {product.note}
                            </div>
                          )}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {product.quantity}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {currencySymbol}
                          {total.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Price Summary */}
        {rows.length > 0 && (
          <div
            style={{
              margin: "10px 0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div>
              <span>Total Items: </span>
              <strong>
                {rows.reduce((sum, p) => sum + Number(p.quantity), 0)}
              </strong>
            </div>

            <div style={{ textAlign: "right" }}>
              <div>
                {taxLabel} ({(gstRate * 100).toFixed(0)}%):{" "}
                <strong>
                  {currencySymbol}
                  {gstAmount.toFixed(2)}
                </strong>
              </div>
              {totalPackageCharge > 0 && (
                <div>
                  Packaging Charges:{" "}
                  <strong>
                    {currencySymbol}
                    {totalPackageCharge.toFixed(2)}
                  </strong>
                </div>
              )}
              <div>
                Total Price:{" "}
                <strong>
                  {currencySymbol}
                  {totalWithGST.toFixed(2)}
                </strong>
              </div>
            </div>
          </div>
        )}

        {/* Driver Selection (ONLY for accept) */}
        {popupOrder.status === "accept" && !isFinalStagePopup && (
          <div
            className="popup-section"
            style={{
              backgroundColor: "#f8f9fa",
              border: "2px solid #007bff",
              borderRadius: "8px",
              padding: "15px",
              marginTop: "15px",
            }}
          >
            <label
              className="popup-label"
              style={{ fontSize: "16px", fontWeight: "bold", color: "#007bff" }}
            >
              📦 Assign Driver for Delivery
            </label>
            <p
              style={{
                fontSize: "13px",
                color: "#6c757d",
                marginBottom: "10px",
              }}
            >
              Select an existing driver or add a new one to proceed with
              delivery pickup
            </p>
            <select
              className="popup-select"
              value={driverId}
              onChange={(e) => setDriverId(e.target.value)}
              style={{ fontSize: "14px", padding: "10px" }}
            >
              <option value="">-- Select Driver --</option>
              {driverList.map((d) => (
                <option key={d._id} value={d._id}>
                  {d.name} - {d.mobile}
                </option>
              ))}
            </select>

            {!useNewDriver && (
              <button
                className="popup-btn secondary"
                onClick={() => setUseNewDriver(true)}
                style={{ marginTop: "10px" }}
              >
                + Add New Driver
              </button>
            )}

            {useNewDriver && (
              <div className="popup-driver-fields">
                <input
                  className="popup-input"
                  placeholder="Driver Name"
                  value={driverName}
                  onChange={(e) => setDriverName(e.target.value)}
                />
                <input
                  className="popup-input"
                  placeholder="Driver Mobile (10 digits)"
                  value={driverMobile}
                  maxLength={10}
                  onChange={(e) =>
                    setDriverMobile(e.target.value.replace(/\D/g, ""))
                  }
                />
              </div>
            )}
          </div>
        )}

        {/* Status Update Buttons */}
        <div className="status-update-buttons">
          {isRejecting && (
            <div className="reject-reason-section">
              <label className="popup-label">
                Select Reason for Rejection:
              </label>

              <select
                className="popup-select"
                value={selectedRejectReason}
                onChange={(e) => setSelectedRejectReason(e.target.value)}
              >
                <option value="">-- Select Reason --</option>
                {reasons.map((reason, index) => (
                  <option key={index} value={reason}>
                    {reason}
                  </option>
                ))}
              </select>

              <div className="popup-button-row">
                <button
                  className="popup-btn danger"
                  onClick={() => {
                    if (!selectedRejectReason) {
                      toast.error("Please select a rejection reason.");
                      return;
                    }
                    updatePopupOrderStatusReject(
                      "reject",
                      selectedRejectReason,
                    );
                  }}
                >
                  Confirm Reject
                </button>

                <button
                  className="popup-btn secondary"
                  onClick={() => {
                    setIsRejecting(false);
                    setSelectedRejectReason("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {!isRejecting && (
            <>
              {getNextStatuses(popupOrder.status).map((status) => {
                if (status === "reject") {
                  return (
                    <button
                      key="reject-btn"
                      className="popup-btn danger"
                      onClick={() => setIsRejecting(true)}
                    >
                      REJECT
                    </button>
                  );
                }

                // Special handling for delivery pickup button when on accept status
                const buttonLabel =
                  popupOrder.status === "accept" && status === "delivery pickup"
                    ? "ASSIGN DRIVER & START PICKUP"
                    : status.toUpperCase();

                return (
                  <button
                    key={status}
                    className="popup-btn primary"
                    style={
                      popupOrder.status === "accept" &&
                        status === "delivery pickup"
                        ? {
                          fontSize: "15px",
                          fontWeight: "bold",
                          padding: "12px 20px",
                        }
                        : {}
                    }
                    onClick={() => {
                      if (
                        popupOrder.status === "accept" &&
                        useNewDriver &&
                        driverMobile.length < 10
                      ) {
                        toast.error(
                          "Please enter a valid 10-digit driver mobile number.",
                        );
                        return;
                      }

                      // Additional validation when moving to delivery pickup
                      if (
                        status === "delivery pickup" &&
                        !driverId &&
                        !useNewDriver
                      ) {
                        toast.error(
                          "Please select a driver or add a new driver before proceeding.",
                        );
                        return;
                      }

                      updatePopupOrderStatus(status);
                    }}
                  >
                    {buttonLabel}
                  </button>
                );
              })}

              {!REPAIR_BLOCKED_STATUSES.includes(popupOrder.status) && (
                <button
                  className="popup-btn repair"
                  onClick={() => {
                    setSelectedOrder(popupOrder);
                    setPopupOrder(null);
                  }}
                >
                  🔧 Modify Order
                </button>
              )}

            </>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default OrderPopup;
